import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { 
  Building2, Mail, Phone, Users, Target, Trash2, Loader2, Search
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import api from '../../lib/api';

export const AdminClubsPage = () => {
  const [clubs, setClubs] = useState([]);
  const [filteredClubs, setFilteredClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    loadClubs();
  }, []);

  useEffect(() => {
    if (search) {
      const filtered = clubs.filter(club => 
        club.name.toLowerCase().includes(search.toLowerCase()) ||
        club.league.toLowerCase().includes(search.toLowerCase()) ||
        club.country.toLowerCase().includes(search.toLowerCase())
      );
      setFilteredClubs(filtered);
    } else {
      setFilteredClubs(clubs);
    }
  }, [search, clubs]);

  const loadClubs = async () => {
    try {
      const response = await api.get('/admin/clubs');
      setClubs(response.data);
      setFilteredClubs(response.data);
    } catch (error) {
      console.error('Error loading clubs:', error);
      toast.error('Error al cargar los clubes');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClub = async (clubId, clubName) => {
    try {
      await api.delete(`/admin/clubs/${clubId}`);
      setClubs(clubs.filter(c => c.id !== clubId));
      toast.success(`Club ${clubName} eliminado`);
    } catch (error) {
      toast.error('Error al eliminar el club');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-clubs-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-800 uppercase tracking-tight">
              Gestión de Clubes
            </h1>
            <p className="text-slate-500 mt-1">
              {clubs.length} clubes registrados en la red
            </p>
          </div>
        </div>

        {/* Search */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Buscar por nombre, liga o país..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-clubs"
            />
          </div>
        </div>

        {/* Clubs Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-6 py-4 font-heading text-xs uppercase tracking-wider text-slate-500">Club</th>
                  <th className="text-left px-6 py-4 font-heading text-xs uppercase tracking-wider text-slate-500">Contacto</th>
                  <th className="text-left px-6 py-4 font-heading text-xs uppercase tracking-wider text-slate-500">Email</th>
                  <th className="text-left px-6 py-4 font-heading text-xs uppercase tracking-wider text-slate-500">Teléfono</th>
                  <th className="text-center px-6 py-4 font-heading text-xs uppercase tracking-wider text-slate-500">Jugadoras</th>
                  <th className="text-center px-6 py-4 font-heading text-xs uppercase tracking-wider text-slate-500">Necesidades</th>
                  <th className="text-right px-6 py-4 font-heading text-xs uppercase tracking-wider text-slate-500">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredClubs.map((club) => (
                  <tr key={club.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                          <span className="font-heading font-bold text-indigo-600">
                            {club.name.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <p className="font-semibold text-slate-800">{club.name}</p>
                          <p className="text-sm text-slate-500">{club.league} • {club.country}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-slate-700">{club.contact_name}</p>
                    </td>
                    <td className="px-6 py-4">
                      <a href={`mailto:${club.contact_email}`} className="flex items-center gap-2 text-indigo-600 hover:text-indigo-700">
                        <Mail className="w-4 h-4" />
                        {club.contact_email}
                      </a>
                    </td>
                    <td className="px-6 py-4">
                      {club.contact_phone ? (
                        <a href={`tel:${club.contact_phone}`} className="flex items-center gap-2 text-slate-600 hover:text-slate-800">
                          <Phone className="w-4 h-4" />
                          {club.contact_phone}
                        </a>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-lime-100 text-lime-700 rounded-full text-sm font-medium">
                        <Users className="w-3 h-3" />
                        {club.players_count}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">
                        <Target className="w-3 h-3" />
                        {club.needs_count}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-700 hover:bg-red-50">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>¿Eliminar {club.name}?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Esta acción eliminará el club y todas sus publicaciones (jugadoras y necesidades). No se puede deshacer.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDeleteClub(club.id, club.name)}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              Eliminar
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredClubs.length === 0 && (
            <div className="p-12 text-center">
              <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No se encontraron clubes</p>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AdminClubsPage;
